import React, { useState, useRef, useEffect } from "react";
import "../css/select.css";
import DropdownIcon from "../icons/DropdownIcon";
import { IIconStyles } from "../types/salaryRange";

interface IProps {
  options: { label: string; value: string; Icon: JSX.Element }[];
  selectedOption: string;
  onChange: (option: string) => void;
  placeholder?: string;
  iconStyles?: IIconStyles;
}

const Select: React.FC<IProps> = ({
  options = [],
  selectedOption,
  onChange,
  placeholder = "Currency",
  iconStyles,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleSelect = (value: string) => {
    onChange(value);
    setIsOpen(false);
    setSearchText("");
    setHighlightedIndex(null);
  };

  const selectedData = options.find((opt) => opt.value === selectedOption);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchText(""); // Reset search when closing
        setHighlightedIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle key press for searching
  useEffect(() => {
    if (!isOpen) return;

    let timer: NodeJS.Timeout;
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key.length === 1) {
        setSearchText((prev) => prev + event.key);
        timer = setTimeout(() => {
          setSearchText("");
          setHighlightedIndex(null);
        }, 1000);
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
      clearTimeout(timer);
    };
  }, [isOpen]);

  // Scroll to and highlight the matched option
  useEffect(() => {
    if (searchText) {
      const matchIndex = options.findIndex((opt) =>
        opt.label.toLowerCase().startsWith(searchText.toLowerCase())
      );
      if (matchIndex !== -1 && optionRefs.current[matchIndex]) {
        optionRefs.current[matchIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
        setHighlightedIndex(matchIndex);
      }
    }
  }, [searchText, options]);

  return (
    <div className="salaryRange_customSelectContainer" ref={selectRef}>
      <div className="salaryRange_selectedOptionDisplay" onClick={() => setIsOpen(!isOpen)}>
        <div className="salaryRange_selectedItemLabel">
          {selectedData ? (
            <>
              {selectedData?.Icon}{" "}
              <p className="salaryRange_selectedLabel">{selectedData.label}</p>
            </>
          ) : (
            <span className="salaryRange_placeholder">{placeholder}</span>
          )}
        </div>
        <span className={`salaryRange_customSelectIcon ${isOpen ? "rotate" : ""}`}>
          <DropdownIcon
            fill={iconStyles?.fill}
            height={iconStyles?.height}
            stroke={iconStyles?.stroke}
            strokeWidth={iconStyles?.strokeWidth}
            width={iconStyles?.width}
          />
        </span>
      </div>

      {isOpen && (
        <div className="salaryRange_customDropdown">
          {options?.length ? (
            options.map((option, index) => (
              <div
                key={option.value}
                ref={(el) => (optionRefs.current[index] = el)}
                className={`salaryRange_dropdownOption ${
                  highlightedIndex === index ? "highlighted" : ""
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.Icon} {option.label}
              </div>
            ))
          ) : (
            <div className={`salaryRange_dropdownOption`}>No Options</div>
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
